import { makeAutoObservable, runInAction } from "mobx";
import { mockRecordSpaceVisit, useLongfansMock } from "../../global/GlobalMock";
import { globalModel } from "../../global/GlobalModel";
import { SpaceApi } from "./SpaceApi";
import type {
  GetHomeDataResult,
  MembershipProduct,
  PcMoreServicesEntrance,
  SpaceModuleConfig,
  SpaceNavItem,
  SpaceProfile,
  UserSubscriptionItem,
} from "./SpaceType";
import type { MemberBadge } from "../../components/member-badge/MemberBadgeType";
import { normalizeIntelCommentKind } from "./SpaceType";
import { applyTrialTierVisibility } from "./trialTierUtils";
import { buildSpacePcNavList, flattenNavLeaves } from "./spacePcNav";

export { buildSpacePcNavList } from "./spacePcNav";

export function mergeModulesWithSubscriptions(
  modules: SpaceModuleConfig[],
  subs: UserSubscriptionItem[] | null
): MembershipProduct[] {
  if (subs == null || subs.length === 0) {
    return modules.map((m) => ({
      ...m,
      userSubscribed: false,
      daysRemaining: null,
      trialTierVisible: true,
      purchaseBlocked: false,
      purchaseBlockHint: null,
      upgradeEligible: false,
      upgradeSourceSpaceProductId: null,
      upgradeSourceDisplayName: null,
    }));
  }
  const byId = new Map(subs.map((s) => [s.spaceProductId, s]));
  return modules.map((m) => {
    const s = byId.get(m.spaceProductId);
    const visible = s?.trialTierVisible !== false;
    const merged = applyTrialTierVisibility(m, visible);
    return {
      ...merged,
      userSubscribed: s?.userSubscribed ?? false,
      daysRemaining: s?.daysRemaining ?? null,
      subscribedStrategyCount: s?.subscribedStrategyCount ?? null,
      strategyEntitlements: s?.strategyEntitlements ?? null,
      trialTierVisible: visible,
      purchaseBlocked: s?.purchaseBlocked ?? false,
      purchaseBlockHint: s?.purchaseBlockHint ?? null,
      upgradeEligible: s?.upgradeEligible ?? false,
      upgradeSourceSpaceProductId: s?.upgradeSourceSpaceProductId ?? null,
      upgradeSourceDisplayName: s?.upgradeSourceDisplayName ?? null,
    };
  });
}

/**
 * 将地址栏 segment 解析为导航项：
 * - 新版：segment 为 spaceProductId（与 routeKey 一致）
 * - 兼容旧版：segment 为 productKind（如 group_companion），若有多个同类实例优先选已订阅项
 */
export function resolveNavItemForSection(
  section: string | undefined,
  navItems: SpaceNavItem[],
  membershipProducts: MembershipProduct[]
): SpaceNavItem | null {
  if (!section) {
    return null;
  }
  const leaves = flattenNavLeaves(navItems);
  const direct = leaves.find((n) => n.routeKey === section);
  if (direct) {
    return direct;
  }
  const kindMatches = leaves.filter((n) => n.productKind === section);
  if (kindMatches.length === 0) {
    return null;
  }
  if (kindMatches.length === 1) {
    return kindMatches[0];
  }
  const subscribedFirst = kindMatches.find((n) => {
    const m = membershipProducts.find((x) => x.spaceProductId === n.spaceProductId);
    return m?.userSubscribed;
  });
  return subscribedFirst ?? kindMatches[0];
}

/** 打包产品无独立栏目页，segment 为其 spaceProductId 或 productKind 时应回空间首页 */
export function isProductBundleSection(
  section: string | undefined,
  membershipProducts: MembershipProduct[]
): boolean {
  if (!section || section === "home") {
    return false;
  }
  if (section === "product_bundle") {
    return true;
  }
  return membershipProducts.some(
    (p) => p.productKind === "product_bundle" && p.spaceProductId === section
  );
}

/** 供 SpacePage 校验路由 segment 是否在导航中存在 */
export function isValidSection(
  section: string | undefined,
  navItems: SpaceNavItem[]
): boolean {
  if (!section) {
    return false;
  }
  if (section === "home") {
    return navItems.some((n) => n.routeKey === "home");
  }
  const leaves = flattenNavLeaves(navItems);
  return leaves.some((n) => n.routeKey === section || n.productKind === section);
}

function normalizeStartupModule(raw: SpaceModuleConfig): SpaceModuleConfig {
  const isGift = raw.pricingMode?.trim().toLowerCase() === "gift";
  const giftNames = Array.isArray(raw.giftTriggerDisplayNames)
    ? raw.giftTriggerDisplayNames
        .map((s) => (typeof s === "string" ? s.trim() : ""))
        .filter((s) => s !== "")
    : undefined;
  const giftMode =
    typeof raw.giftMemberMatchMode === "string" && raw.giftMemberMatchMode.trim() !== ""
      ? raw.giftMemberMatchMode.trim().toLowerCase()
      : undefined;
  const legacyMore =
    (raw as SpaceModuleConfig & { pcAvatarDropdown?: PcMoreServicesEntrance })
      .pcAvatarDropdown;
  const moreRaw = raw.pcMoreServices ?? legacyMore;
  const pcMoreServices =
    moreRaw && typeof moreRaw === "object"
      ? {
          enabled: !!moreRaw.enabled,
          sortOrder:
            typeof moreRaw.sortOrder === "number" && moreRaw.sortOrder >= 1
              ? moreRaw.sortOrder
              : 1,
        }
      : { enabled: false, sortOrder: 1 };
  const homeRaw = raw.homeSubscribeCard;
  const homeSubscribeCard =
    homeRaw && typeof homeRaw === "object"
      ? {
          enabled: !!homeRaw.enabled,
          sortOrder:
            typeof homeRaw.sortOrder === "number" && homeRaw.sortOrder >= 1
              ? homeRaw.sortOrder
              : 1,
        }
      : undefined;
  return {
    ...raw,
    pcMoreServices,
    homeSubscribeCard,
    pricingMode: raw.pricingMode?.trim() ? raw.pricingMode : "retail",
    priceSpecs: Array.isArray(raw.priceSpecs) ? raw.priceSpecs : [],
    agreementMode: raw.agreementMode?.trim() ? raw.agreementMode : "none",
    agreementTemplateId: raw.agreementTemplateId?.trim()
      ? raw.agreementTemplateId
      : "",
    giftTriggerDisplayNames:
      isGift && giftNames && giftNames.length > 0 ? giftNames : undefined,
    giftMemberMatchMode: isGift ? giftMode : undefined,
    giftPurchaseHint:
      isGift && typeof raw.giftPurchaseHint === "string" && raw.giftPurchaseHint.trim() !== ""
        ? raw.giftPurchaseHint.trim()
        : undefined,
    intelCommentKind: normalizeIntelCommentKind(raw.intelCommentKind),
    qaAllowPublic: raw.productKind === "qa" ? raw.qaAllowPublic === true : undefined,
  };
}

export class SpaceModel {
  spaceId = "";
  spaceModules: SpaceModuleConfig[] = [];
  membershipProducts: MembershipProduct[] = [];
  navItems: SpaceNavItem[] = [];
  configLoadError: string | null = null;
  loaded = false;

  spaceProfile: SpaceProfile | null = null;
  homeData: GetHomeDataResult | null = null;

  favorStatus: 0 | 1 = 0;
  /** 当前登录用户在本空间的会员身份标识 */
  myMemberBadges: MemberBadge[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  setSpaceId(id: string) {
    this.spaceId = id;
  }

  resetFavorState() {
    this.favorStatus = 0;
  }

  async loadFavorState() {
    const id = this.spaceId;
    if (!id || !globalModel.isLoggedIn) {
      runInAction(() => {
        this.favorStatus = 0;
      });
      return;
    }
    const s = await SpaceApi.getSpaceFavorState(id);
    runInAction(() => {
      this.favorStatus = s?.favorStatus === 1 ? 1 : 0;
    });
  }

  async refreshPublicProfile() {
    const id = this.spaceId;
    if (!id) {
      return;
    }
    const profileRes = await SpaceApi.getSpaceProfile(id);
    runInAction(() => {
      if (profileRes && typeof profileRes !== "string") {
        this.spaceProfile = profileRes;
      }
    });
  }

  async submitFavor(want: boolean): Promise<boolean> {
    const id = this.spaceId;
    if (!id) {
      return false;
    }
    const ok = await SpaceApi.setSpaceFavor(id, want);
    if (ok) {
      await this.loadFavorState();
      await this.refreshPublicProfile();
      // 关注免费产品依赖关注态判定权益，需立即刷新订阅合并结果
      await this.refreshSubscriptionMerge();
    }
    return ok;
  }

  async refreshSubscriptionMerge() {
    const id = this.spaceId;
    if (!id || this.spaceModules.length === 0) {
      return;
    }
    if (!globalModel.isLoggedIn) {
      runInAction(() => {
        this.myMemberBadges = [];
        this.membershipProducts = mergeModulesWithSubscriptions(
          this.spaceModules,
          null
        );
        this.navItems = buildSpacePcNavList(this.membershipProducts);
      });
      return;
    }
    const subRes = await SpaceApi.getSubscriptionDetail(id);
    runInAction(() => {
      if (typeof subRes === "string") {
        this.myMemberBadges = [];
        this.membershipProducts = mergeModulesWithSubscriptions(
          this.spaceModules,
          null
        );
      } else {
        this.myMemberBadges = subRes.myMemberBadges ?? [];
        this.membershipProducts = mergeModulesWithSubscriptions(
          this.spaceModules,
          subRes.items ?? []
        );
      }
      this.navItems = buildSpacePcNavList(this.membershipProducts);
    });
  }

  async loadConfig() {
    this.configLoadError = null;
    const id = this.spaceId;
    if (!id) {
      return;
    }
    runInAction(() => {
      this.loaded = false;
      this.spaceModules = [];
      this.membershipProducts = [];
      this.navItems = buildSpacePcNavList([]);
    });

    const [cfgRes, profileRes, homeRes] = await Promise.all([
      SpaceApi.getStartupConfig(id),
      SpaceApi.getSpaceProfile(id),
      SpaceApi.getSpaceHomeData(id),
    ]);

    if (typeof cfgRes === "string") {
      runInAction(() => {
        this.spaceModules = [];
        this.membershipProducts = [];
        this.navItems = buildSpacePcNavList([]);
        this.configLoadError = cfgRes;
        this.spaceProfile = profileRes;
        this.homeData = homeRes;
        this.loaded = true;
      });
      if (useLongfansMock && globalModel.isLoggedIn) {
        mockRecordSpaceVisit(id);
      }
      if (globalModel.isLoggedIn) {
        void globalModel.loadRecentSpaces();
      }
      return;
    }

    const modules = cfgRes.map(normalizeStartupModule);
    runInAction(() => {
      this.spaceModules = modules;
      this.configLoadError = null;
      this.spaceProfile = profileRes;
      this.homeData = homeRes;
      this.loaded = true;
    });
    if (useLongfansMock && globalModel.isLoggedIn) {
      mockRecordSpaceVisit(id);
    }
    if (globalModel.isLoggedIn) {
      void globalModel.loadRecentSpaces();
    }
  }
}
